import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { SectionHeading } from './SectionHeading';
import { useT } from '../i18n/I18nProvider';

// Color palette for programme cards (rotates)
const ACCENTS = [
  { ring: 'from-amber-400 to-orange-500', text: 'text-orange-600' },
  { ring: 'from-rose-400 to-pink-500', text: 'text-pink-600' },
  { ring: 'from-emerald-400 to-teal-500', text: 'text-teal-600' },
  { ring: 'from-violet-400 to-fuchsia-500', text: 'text-fuchsia-600' },
  { ring: 'from-sky-400 to-blue-500', text: 'text-blue-600' },
  { ring: 'from-yellow-400 to-amber-500', text: 'text-amber-600' },
];

const Programmes = () => {
  const t = useT();
  const { data: programmes, loading } = useContent('projects', []);
  const items = programmes || [];
  if (!loading && items.length === 0) return null;

  return (
    <section id="programmes" className="py-24 bg-gradient-to-b from-white via-amber-50/30 to-white relative overflow-hidden" data-testid="programmes-section">
      {/* decorative blobs */}
      <div className="absolute top-20 -left-20 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 -right-20 w-72 h-72 bg-rose-200/30 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4">
        <SectionHeading
          eyebrow={t('programmes.eyebrow')}
          title={t('programmes.title')}
          accent={t('programmes.title_accent')}
          subtitle={t('programmes.subtitle')}
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((programme, idx) => {
            const accent = ACCENTS[idx % ACCENTS.length];
            return (
              <Card
                key={programme.id}
                className="group relative overflow-hidden border-2 border-gray-100 hover:border-transparent transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
              >
                {/* Gradient top bar */}
                <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${accent.ring}`}></div>
                {/* Number badge */}
                <div className={`absolute top-4 right-4 w-10 h-10 rounded-full bg-gradient-to-br ${accent.ring} text-white flex items-center justify-center font-bold text-sm shadow-lg`}>
                  {String(idx + 1).padStart(2, '0')}
                </div>

                <CardContent className="p-8 pt-10">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${accent.ring} text-white text-3xl mb-5 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform`}>
                    {programme.icon}
                  </div>
                  {programme.badge && (
                    <Badge className={`mb-3 bg-gray-50 ${accent.text} border border-gray-200 text-xs font-semibold uppercase tracking-wider`}>
                      {programme.badge}
                    </Badge>
                  )}
                  <h3 className="text-xl md:text-2xl font-bold text-[#0A1628] mb-3 group-hover:text-[#1565C0] transition-colors">
                    {programme.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 mb-5 leading-relaxed line-clamp-4">
                    {programme.description}
                  </p>
                  <a
                    href={programme.link || '#programmes'}
                    className={`inline-flex items-center gap-2 ${accent.text} text-sm font-bold uppercase tracking-wider hover:gap-3 transition-all`}
                  >
                    {t('programmes.learn_more')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Programmes;
